import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import Axios from "axios";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";

@Component
export default class AnnotationRevertMixin extends Vue {
    protected allowAnnotationRevert = false;
    protected sourceImageId: number | null = null;
    protected pendingSourceImageId: number | null = null;
    protected revertingImage = false;

    async fetchSourceImageId(imageId: number) {
        if (!this.allowAnnotationRevert) {
            this.pendingSourceImageId = imageId;
            this.sourceImageId = null;
            return;
        }
        this.pendingSourceImageId = null;
        try {
            const resp = await Axios.get(`/api/Image/${imageId}/source`);
            this.sourceImageId = resp.data.sourceImageId ?? null;
        } catch {
            this.sourceImageId = null;
        }
    }

    protected clearSourceImageId() {
        this.sourceImageId = null;
        this.pendingSourceImageId = null;
    }

    // Refresh the on-screen image after a revert. Overridable: a page whose ImageComponent lives in
    // a child component (e.g. RxImagePanel) overrides this to route through that child instead of
    // reaching into $refs.prescriptionImage directly.
    protected refreshRevertedImage(imageId: number | null) {
        const imageComponent = this.$refs.prescriptionImage as any;
        if (imageComponent) {
            imageComponent.fetchPrescriptionImageSource(`image/${imageId}`);
        }
    }

    protected retryPendingSourceFetch() {
        if (this.allowAnnotationRevert && this.pendingSourceImageId !== null) {
            void this.fetchSourceImageId(this.pendingSourceImageId);
        }
    }

    async revertToOriginalImage(prescription: { storeID: number; rxNumber?: number; rfNumber: number; imageID: number | null }) {
        if (!prescription || !this.sourceImageId) return;

        const confirmed = await (this as any).$bvModal.msgBoxConfirm(
            "Are you sure you want to revert to the original un-annotated image? This will replace the current annotated image on this prescription.",
            {
                title: "Revert to Original Image",
                okVariant: "warning",
                okTitle: "Revert",
                cancelTitle: "Cancel",
            }
        );

        if (!confirmed) return;

        this.revertingImage = true;
        try {
            // Pre-save (new Rx) has no row to update; the in-memory id links at first save
            if (prescription.rxNumber) {
                await Axios.post("/Prescription/updateImage", {
                    storeID: prescription.storeID,
                    rxNumber: prescription.rxNumber,
                    rfNumber: prescription.rfNumber,
                    newImageId: this.sourceImageId,
                });
            }

            prescription.imageID = this.sourceImageId;
            this.sourceImageId = null;

            this.refreshRevertedImage(prescription.imageID);

            (this as any).$notification(NotificationOptions.success("Image reverted to original successfully."));
        } catch (err: any) {
            (this as any).$notification(NotificationOptions.error(err?.response?.data || "Failed to revert image. Please try again."));
        } finally {
            this.revertingImage = false;
        }
    }
}
